import React, { useEffect, useRef } from 'react';

/**
 * AnalysisCanvasLoader
 * Canvas-based waving flag animation of a dotted logo.
 * - Draws rows of white elliptical dots forming a band, animated with a sine wave.
 * - Defaults are tuned for inline placement next to the ANALYSIS title.
 */
const AnalysisCanvasLoader = ({
  size = 24,              // CSS pixels for square canvas
  dotColor = '#ffffff',   // color of dots (white like logo)
  bgColor = 'transparent',// background fill (transparent for header)
  speed = 2.2,            // wave speed
  className = ''
}) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    const ctx = canvas.getContext('2d');
    const cssSize = size;
    const pxSize = Math.floor(cssSize * dpr);
    canvas.width = pxSize;
    canvas.height = pxSize;
    canvas.style.width = `${cssSize}px`;
    canvas.style.height = `${cssSize}px`;

    // Grid configuration (rows and columns of dots forming the logo band)
    const ROWS = 6;
    const COLS = 14;
    const gapX = pxSize / (COLS + 2.5); // horizontal spacing
    const gapY = pxSize / (ROWS + 3.5); // vertical spacing

    // Row lengths to create tapered ends (curved band look)
    const rowCols = [12, 12, 14, 14, 12, 10];

    // Dot sizes relative to canvas
    const dotW = Math.max(2, Math.floor(pxSize * 0.08));
    const dotH = Math.max(1, Math.floor(pxSize * 0.045));

    // Wave parameters
    const ampX = pxSize * 0.10;     // horizontal undulation amplitude
    const ampY = pxSize * 0.06;     // vertical ripple amplitude
    const phaseRow = Math.PI / 8;   // per-row phase offset
    const phaseCol = Math.PI / 10;  // per-column phase offset

    // Skew to give the band a slanted flow
    const skewXPerRow = gapX * 0.35;

    let start = performance.now();

    const draw = (t) => {
      const time = (t - start) / 1000; // seconds
      ctx.clearRect(0, 0, pxSize, pxSize);

      // Optional background fill (transparent by default)
      if (bgColor && bgColor !== 'transparent') {
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, pxSize, pxSize);
      }

      // Draw dotted band
      for (let r = 0; r < ROWS; r += 1) {
        const colsThisRow = rowCols[r] || COLS;
        const rowYBase = pxSize * 0.25 + r * gapY; // vertical placement of rows

        // Base horizontal wave for the whole row (flag-like sweep)
        const rowWave = Math.sin(time * speed + r * phaseRow) * ampX;

        for (let c = 0; c < colsThisRow; c += 1) {
          // Center columns around middle for each row
          const cCentered = c - (colsThisRow - 1) / 2;

          // Diagonal skew so rows lean forward like a flag
          const skew = r * skewXPerRow;

          // Per-dot vertical ripple to add a subtle flutter
          const dotRipple = Math.sin(time * speed * 1.3 + c * phaseCol + r * phaseRow) * ampY;

          const x = pxSize * 0.5 + cCentered * gapX + rowWave + skew;
          const y = rowYBase + dotRipple + Math.cos((c / Math.max(1, colsThisRow - 1)) * Math.PI) * (gapY * 0.15);

          // Draw an elliptical dot (slightly rotated look via scaling)
          ctx.save();
          ctx.translate(x, y);
          ctx.fillStyle = dotColor;
          ctx.beginPath();
          // Draw ellipse using scale trick
          ctx.scale(1, 0.78);
          ctx.arc(0, 0, dotW / 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [size, dotColor, bgColor, speed]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden
      role="img"
    />
  );
};

export default AnalysisCanvasLoader;
